import React, { useContext } from 'react';
import moment from 'moment/moment';
import { SubmissionContext } from '../Context/SubmissionProvider';
import SubmissionHeaderAlert from '../../../../../../common/Styleguide/Common/Alert';
import { GAME_GUIDELINES_URL } from './UpdateSubmission.types';
import { DOCS_URL } from '../../../../SideMenu/Menu';
import { Box } from '@mui/system';
import { getLatestBuild } from '../../../../../../common/build-utils';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';

const SubmissionStatusHeader: React.FC = () => {
  const { submission, isNonEditable } = useContext(SubmissionContext);

  if (!submission) {
    return null;
  }

  // developer can go to QA his latest DRAFT build
  // in this case also show an alert to remind them about this
  const descendingBuilds = [...submission.gameBuilds].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));
  const showGoToQAInfo = descendingBuilds[0]?.status === 'DRAFT';

  const renderWaitingForReview = () => {
    return (
      <SubmissionHeaderAlert color="purple" title="Waiting for review" key={'waitingForReview'}>
        Your game has been sent for review. We will notify you once the team has reviewed your game.
      </SubmissionHeaderAlert>
    );
  };

  const renderRejected = () => {
    return (
      <SubmissionHeaderAlert color="red" title="Your submission has been rejected" key={'rejected'}>
        Unfortunately, your game has been rejected. Make sure to read{' '}
        <a target="_blank" rel="noopener noreferrer" href={GAME_GUIDELINES_URL}>
          our documentation
        </a>{' '}
        to find all the guidelines and requirements regarding game submissions. Due to the large number of submissions we receive every day,
        we are unable to give you personalised feedback. Thank you for your understanding.
      </SubmissionHeaderAlert>
    );
  };

  const renderFeedback = () => {
    const { feedback } = submission;
    if (!feedback || feedback?.length < 1) {
      return null;
    }
    const lastFeedback = feedback.at(-1);
    const lastFeedbackDate = moment.utc(lastFeedback?.postedOn).format('DD.MM.YYYY');
    const title = <>Feedback from {lastFeedbackDate}</>;
    return (
      <SubmissionHeaderAlert color="orange" title={title} key={'feedback'}>
        Here's the feedback from our team:
        <br />
        <p>{lastFeedback?.comment && <em dangerouslySetInnerHTML={{ __html: lastFeedback?.comment.replaceAll('\n', '<br />') }} />}</p>
        {submission.status === 'BASIC_LAUNCH_APPROVED'
          ? 'Please submit a new version taking into account the Full Implementation requirements and our feedback.'
          : 'Please submit a new version with those change.'}
      </SubmissionHeaderAlert>
    );
  };

  const renderApprovedForFullLaunch = () => {
    return (
      <SubmissionHeaderAlert color="green" title="Congratulations!" key={'approvedForFullLaunch'}>
        Your game has been approved for Full Launch on CrazyGames. Please update your game to meet the requirements for the{' '}
        <a href={`${DOCS_URL}/requirements/technical/`} target="_blank" rel="noreferrer">
          Full Implementation
        </a>
        .
      </SubmissionHeaderAlert>
    );
  };

  const renderGoToQA = () => {
    return (
      <SubmissionHeaderAlert color="orange" title="Latest changes require QA" key={'goToQA'}>
        You didn't pass QA with your latest changes. Our content team will review them only after you passed the QA tool and submitted your
        update.
      </SubmissionHeaderAlert>
    );
  };

  const alerts: (JSX.Element | null)[] = [];

  // Only show the alert if the game has just been approved for full launch and the developer has not updated the game since
  const approvedForFullLaunch = submission.status === 'BASIC_LAUNCH_APPROVED' && submission.isFullLaunch;

  if (showGoToQAInfo) {
    alerts.push(renderGoToQA());
  }

  if (approvedForFullLaunch) {
    alerts.push(renderApprovedForFullLaunch());
  }

  if (submission.status === 'NEW_SUBMISSION' || submission.status === 'SYNC_REQUIRED') {
    alerts.push(renderWaitingForReview());
  }

  const showFeedbackStatuses: SUBMISSION_STATUS[] = [
    'AWAITING_DEVELOPER_CHANGE',
    'BASIC_LAUNCH_MIXED_RESULT',
    'BASIC_LAUNCH_APPROVED',
    'BASIC_LAUNCH_AWAITING_DEVELOPER_CHANGE',
  ];
  if (
    !isNonEditable &&
    (showFeedbackStatuses.includes(submission.status) || getLatestBuild(submission.gameBuilds)?.status === 'REJECTED')
  ) {
    alerts.push(renderFeedback());
  }

  if (submission.status === 'REJECTED') {
    alerts.push(renderRejected());
  }

  return (
    <Box display={'flex'} flexDirection={'column'} width={'100%'}>
      {alerts.map((alert) => alert)}
    </Box>
  );
};

export default SubmissionStatusHeader;
